import { useState } from "react";
import {
  Autocomplete,
  Button,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
} from "@mui/material";
import { Search, TuneRounded } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { Employee } from "../../process/types/processTypes";
import { Company } from "../../setups/types/setupTypes";
import { WALL_CATEGORIES } from "../wallConstants";
import { employeeLabel, searchCompanies, searchEmployees } from "../wallSearch";
import { WallCategory, WallUser } from "../types/wallTypes";

export type WallFilterValues = {
  category?: WallCategory;
  employeeId?: string;
  brokerCompanyId?: string;
  authorId?: string;
  mentionId?: string;
  tag?: string;
  searchText?: string;
};

type Props = {
  officeUsers: WallUser[];
  onChange: (values: WallFilterValues) => void;
};

export default function WallFilters({ officeUsers, onChange }: Props) {
  const { t } = useTranslation();
  const { t: te } = useTranslation("enums");

  const [category, setCategory] = useState<WallCategory | "">("");
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [broker, setBroker] = useState<Company | null>(null);
  const [authorId, setAuthorId] = useState("");
  const [mentionId, setMentionId] = useState("");
  const [searchText, setSearchText] = useState("");

  const [employeeOptions, setEmployeeOptions] = useState<Employee[]>([]);
  const [companyOptions, setCompanyOptions] = useState<Company[]>([]);

  const emit = (override: Partial<WallFilterValues> = {}) => {
    onChange({
      category: category || undefined,
      employeeId: employee?._id,
      brokerCompanyId: broker?._id,
      authorId: authorId || undefined,
      mentionId: mentionId || undefined,
      searchText: searchText || undefined,
      ...override,
    });
  };

  const clear = () => {
    setCategory("");
    setEmployee(null);
    setBroker(null);
    setAuthorId("");
    setMentionId("");
    setSearchText("");
    onChange({});
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": { borderRadius: 99, bgcolor: "background.paper" },
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        p: 1.5,
        borderRadius: 4,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.default",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", md: "center" }}
        sx={{ flexWrap: "wrap" }}
      >
        <TuneRounded sx={{ color: "text.disabled", ml: 0.5, display: { xs: "none", md: "block" } }} />

        {/* <TextField
          select
          size="small"
          label={t("byCategory")}
          value={category}
          onChange={(e) => {
            const v = (e.target.value as WallCategory) || "";
            setCategory(v);
            emit({ category: v || undefined });
          }}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="">{t("all")}</MenuItem>
          {WALL_CATEGORIES.map((c) => (
            <MenuItem key={c} value={c}>
              {te(`wallCategory.${c}`)}
            </MenuItem>
          ))}
        </TextField> */}

        <Autocomplete
          size="small"
          options={employeeOptions}
          value={employee}
          filterOptions={(x) => x}
          getOptionLabel={(o) => employeeLabel(o as Employee)}
          isOptionEqualToValue={(o, v) => o._id === v._id}
          onInputChange={async (_, val) => {
            if (val.length >= 2) setEmployeeOptions(await searchEmployees(val));
          }}
          onChange={(_, v) => {
            setEmployee(v as Employee | null);
            emit({ employeeId: (v as Employee)?._id });
          }}
          sx={{ minWidth: 200, flex: 1, ...fieldSx }}
          renderInput={(params) => (
            <TextField {...params} label={t("byEmployee")} />
          )}
        />

        {/* <Autocomplete
          size="small"
          options={companyOptions}
          value={broker}
          filterOptions={(x) => x}
          getOptionLabel={(o) => (o as Company).displayName}
          isOptionEqualToValue={(o, v) => o._id === v._id}
          onInputChange={async (_, val) => {
            if (val.length >= 2) setCompanyOptions(await searchCompanies(val));
          }}
          onChange={(_, v) => {
            setBroker(v as Company | null);
            emit({ brokerCompanyId: (v as Company)?._id });
          }}
          sx={{ minWidth: 200 }}
          renderInput={(params) => (
            <TextField {...params} label={t("byBroker")} />
          )}
        /> */}

        <TextField
          select
          size="small"
          label={t("byAuthor")}
          value={authorId}
          onChange={(e) => {
            setAuthorId(e.target.value);
            emit({ authorId: e.target.value || undefined });
          }}
          sx={{ minWidth: 170, flex: 1, ...fieldSx }}
        >
          <MenuItem value="">{t("all")}</MenuItem>
          {officeUsers.map((u) => (
            <MenuItem key={u._id} value={u._id}>
              {u.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label={t("byMention")}
          value={mentionId}
          onChange={(e) => {
            setMentionId(e.target.value);
            emit({ mentionId: e.target.value || undefined });
          }}
          sx={{ minWidth: 170, flex: 1, ...fieldSx }}
        >
          <MenuItem value="">{t("all")}</MenuItem>
          {officeUsers.map((u) => (
            <MenuItem key={u._id} value={u._id}>
              {u.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          size="small"
          label={t("search")}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") emit({ searchText: searchText || undefined });
          }}
          sx={{ minWidth: 170, flex: 1, ...fieldSx }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" sx={{ color: "text.disabled" }} />
              </InputAdornment>
            ),
          }}
        />

        <Button
          onClick={clear}
          size="small"
          sx={{ borderRadius: 99, textTransform: "none", px: 2.5 }}
        >
          {t("clear")}
        </Button>
      </Stack>
    </Paper>
  );
}
